import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Gig } from '@stage/contracts';
import { ApiError } from '../api/client.js';
import type { GigAnalytics } from '../api/analyticsClient.js';
import { useAnalyticsClient } from '../hooks/useAnalyticsClient.js';
import { useAdminClient } from '../hooks/useAdminClient.js';
import { formatMetric, formatRate, MISSING } from '../lib/metric.js';
import { formatMinor } from '../lib/money.js';

/**
 * Per-gig analytics dashboard (§26.1): a metric grid of the initial per-gig counts, the QR
 * source-code breakdown (§26.3), gross tips by currency, session conversion, and peak interaction
 * hour. Per §26.2 a metric with no data is rendered as "—" (a `null` count), NEVER as a real `0` —
 * the missing tile is styled distinctly so the two can't be confused.
 */

/** Humanise a camelCase metric key, e.g. `songRequests` → "Song requests". */
function humanize(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function GigAnalyticsPage() {
  const { gigId = '' } = useParams();
  const analytics = useAnalyticsClient();
  const admin = useAdminClient();
  const [gig, setGig] = useState<Gig | null>(null);
  const [data, setData] = useState<GigAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [g, a] = await Promise.all([admin.getGig(gigId), analytics.getGigAnalytics(gigId)]);
      setGig(g);
      setData(a);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load analytics.');
    }
  }, [admin, analytics, gigId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <main className="page">
        <p className="error">{error}</p>
        <Link className="link" to={`/app/gigs/${encodeURIComponent(gigId)}`}>
          Back to gig
        </Link>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="page" aria-busy="true">
        <p>Loading analytics…</p>
      </main>
    );
  }

  const counts = Object.entries(data.counts);
  const tips = data.grossTipByCurrencyMinor ? Object.entries(data.grossTipByCurrencyMinor) : [];

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to={`/app/gigs/${encodeURIComponent(gigId)}`}>
              Gig editor
            </Link>{' '}
            / Analytics
          </p>
          <h1>Analytics</h1>
          {gig && <p className="status">{gig.name}</p>}
        </div>
        <div className="button-row">
          {gig?.artistId && (
            <Link
              className="button-link"
              to={`/app/artists/${encodeURIComponent(gig.artistId)}/analytics`}
            >
              Artist analytics
            </Link>
          )}
          <Link className="button-link" to={`/app/gigs/${encodeURIComponent(gigId)}/operations`}>
            Operations
          </Link>
        </div>
      </header>

      <section className="card">
        <h2>Metrics</h2>
        <p className="help">
          Initial per-gig metrics (§26.1). A metric with no data is shown as {MISSING} — that is not
          a zero (§26.2).
        </p>
        {counts.length === 0 ? (
          <p className="muted">No metric data.</p>
        ) : (
          <ul className="metric-grid">
            {counts.map(([key, value]) => (
              <li
                key={key}
                className={`metric-tile${value === null ? ' metric-tile--missing' : ''}`}
              >
                <span className="metric-tile__value">{formatMetric(value)}</span>
                <span className="metric-tile__label">{humanize(key)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Conversion &amp; timing</h2>
        <dl className="kv">
          <div>
            <dt>Conversion by session</dt>
            <dd>{formatRate(data.conversionRateBySession)}</dd>
          </div>
          <div>
            <dt>Peak interaction hour</dt>
            <dd>{data.peakInteractionHour ?? MISSING}</dd>
          </div>
        </dl>
      </section>

      <section className="card">
        <h2>Gross tips by currency</h2>
        {tips.length === 0 ? (
          <p className="muted">No tip data yet.</p>
        ) : (
          <dl className="tip-summary">
            {tips.map(([currency, amountMinor]) => (
              <div key={currency}>
                <dt>{currency}</dt>
                <dd>
                  {formatMinor(amountMinor)} {currency}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      <section className="card">
        <h2>Source-code breakdown</h2>
        <p className="help">QR placement visits by artist-created source code (§26.3).</p>
        {!data.sourceBreakdown || data.sourceBreakdown.length === 0 ? (
          <p className="muted">No source-code data yet.</p>
        ) : (
          <ul className="ranked-list">
            {data.sourceBreakdown.map((s) => (
              <li key={s.sourceCode} className="ranked-row">
                <span className="ranked-row__label">{s.sourceCode}</span>
                <span className="ranked-row__count">{formatMetric(s.count)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
